/**
 * Trade-offer card. Single 200×36 parchment row laid out as:
 *
 *   ┌─┐
 *   │A│ @author
 *   └─┘ [icon] ×N + [icon] ×N → [icon] ×N      │tier│
 *
 *   - Author avatar in a small framed square box, circle-cropped (top-left).
 *   - Username on the same row as the avatar, to its right.
 *   - Single trade line BELOW the name: cost resources, a `+` between them
 *     if multiple, the arrow `→`, then receive resources. Same font, single
 *     visual rhythm so the player can parse a trade at a glance.
 *   - Tier strip on the right edge (4 px wide) — green = own-now, cyan
 *     glowing = projected, dim = out of reach. The whole card desaturates
 *     when out of reach.
 */

import Phaser from "phaser";

import { PALETTE, UGC_CYAN } from "../config";
import type { AuthorIdentity, OfferAffordabilityTier, Resource, TradeOffer } from "../types";

import type { AuthorIdentityService } from "../services/AuthorIdentityService";

import { ensureAllShopIcons, iconKeyForResource, isSkillResource, resourceLabel } from "./shopIcons";

const CARD_WIDTH = 200;
const CARD_HEIGHT = 36;
const AVATAR_BOX_SIZE = 18;
const AVATAR_INNER_SIZE = 14;
const AVATAR_BOX_X = 4;
const AVATAR_BOX_Y = 4;
const NAME_X = AVATAR_BOX_X + AVATAR_BOX_SIZE + 4;
const NAME_Y = 5;
const TRADE_ROW_X = AVATAR_BOX_X + 2;
const TRADE_ROW_Y = AVATAR_BOX_Y + AVATAR_BOX_SIZE + 2;
const TIER_STRIP_WIDTH = 4;

export class OfferCard {
  readonly container: Phaser.GameObjects.Container;

  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly highlight: Phaser.GameObjects.Rectangle;
  private readonly tierStrip: Phaser.GameObjects.Rectangle;
  private readonly tradeRow: Phaser.GameObjects.Container;
  private readonly nameLabel: Phaser.GameObjects.Text;
  private readonly countdownLabel: Phaser.GameObjects.Text;
  private bobTween?: Phaser.Tweens.Tween;

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    offer: TradeOffer,
    identity: AuthorIdentity,
    identityService: AuthorIdentityService
  ) {
    ensureAllShopIcons(scene);

    this.container = scene.add.container(x, y);

    this.bg = scene.add
      .rectangle(0, 0, CARD_WIDTH, CARD_HEIGHT, PALETTE.shopCardBg, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.shopCardEdge);

    this.highlight = scene.add
      .rectangle(0, 0, CARD_WIDTH, CARD_HEIGHT, UGC_CYAN, 0)
      .setOrigin(0, 0);

    this.tierStrip = scene.add
      .rectangle(CARD_WIDTH - TIER_STRIP_WIDTH - 1, 2, TIER_STRIP_WIDTH - 1, CARD_HEIGHT - 4, PALETTE.shopTierOut, 1)
      .setOrigin(0, 0);

    // Avatar box (framed square) + circle-cropped avatar inside.
    const avatarBox = scene.add
      .rectangle(AVATAR_BOX_X, AVATAR_BOX_Y, AVATAR_BOX_SIZE, AVATAR_BOX_SIZE, PALETTE.authorBadgeBg, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.shopCardEdge);
    const avatarOffset = (AVATAR_BOX_SIZE - AVATAR_INNER_SIZE) / 2;
    const avatar = identityService.drawCircleAvatar(
      scene,
      AVATAR_BOX_X + avatarOffset,
      AVATAR_BOX_Y + avatarOffset,
      identity,
      AVATAR_INNER_SIZE
    );

    this.nameLabel = scene.add
      .text(NAME_X, NAME_Y, this.truncateName(offer.authorUsername), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.ink),
        fontStyle: "bold",
      })
      .setOrigin(0, 0);

    // Countdown — top-right, same y as the name. Right-aligned via origin(1,0).
    // Sits just left of the tier strip so it never overlaps the tier indicator.
    this.countdownLabel = scene.add
      .text(CARD_WIDTH - TIER_STRIP_WIDTH - 4, NAME_Y, "", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.ink),
      })
      .setOrigin(1, 0);

    this.tradeRow = scene.add.container(TRADE_ROW_X, TRADE_ROW_Y);
    this.renderTradeRow(offer);

    // Assembly order — background → highlight → tier strip → avatar box →
    // avatar texture → name → trade row. Everything that should appear in
    // front of the parchment bg goes AFTER it in this list. Critical fix:
    // the avatarBox is part of this single add() call, not a separate
    // earlier .add(avatarBox) which would land behind the bg.
    this.container.add([
      this.bg,
      this.highlight,
      this.tierStrip,
      avatarBox,
      avatar,
      this.nameLabel,
      this.countdownLabel,
      this.tradeRow,
    ]);
  }

  /** Update the countdown readout. Pass remaining lifetime in ms. Below 30 s
   *  the text reads red so the player notices imminent expiry. */
  setCountdown(remainingMs: number): void {
    const sec = Math.max(0, Math.ceil(remainingMs / 1000));
    const mm = Math.floor(sec / 60);
    const ss = sec % 60;
    this.countdownLabel.setText(`${mm}:${ss.toString().padStart(2, "0")}`);
    this.countdownLabel.setColor(this.toHex(sec < 30 ? 0xb04040 : PALETTE.ink));
  }

  /** Brief slide-in + cyan flash for a freshly arrived offer. Called by
   *  ShopScene whenever a new offer pushes onto buffer[0]. */
  animateArrive(): void {
    const originalY = this.container.y;
    this.container.y = originalY - 40;
    this.container.alpha = 0;
    this.scene.tweens.add({
      targets: this.container,
      y: originalY,
      alpha: 1,
      duration: 280,
      ease: "Back.easeOut",
    });
    this.scene.tweens.add({
      targets: this.bg,
      fillColor: { from: 0x7dd9f5, to: PALETTE.shopCardBg },
      duration: 460,
      ease: "Cubic.easeOut",
    });
  }

  setTier(tier: OfferAffordabilityTier): void {
    if (tier === "own") {
      this.tierStrip.fillColor = PALETTE.shopTierOwn;
      this.bg.setAlpha(1);
      this.stopBob();
    } else if (tier === "projected") {
      this.tierStrip.fillColor = PALETTE.shopTierProjected;
      this.bg.setAlpha(1);
      this.startBob();
    } else {
      this.tierStrip.fillColor = PALETTE.shopTierOut;
      this.bg.setAlpha(0.45);
      this.stopBob();
    }
  }

  setHighlighted(highlighted: boolean): void {
    this.highlight.setFillStyle(UGC_CYAN, highlighted ? 0.22 : 0);
    this.bg.setStrokeStyle(highlighted ? 2 : 1, highlighted ? UGC_CYAN : PALETTE.shopCardEdge);
  }

  flashRejected(): void {
    this.scene.tweens.add({
      targets: this.bg,
      fillColor: { from: 0xff4d4d, to: PALETTE.shopCardBg },
      duration: 320,
      ease: "Cubic.easeOut",
    });
  }

  destroy(): void {
    this.stopBob();
    this.container.destroy();
  }

  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  tweenTo(x: number, y: number, duration: number, onComplete?: () => void): void {
    this.scene.tweens.add({
      targets: this.container,
      x,
      y,
      duration,
      ease: "Quad.easeOut",
      onComplete,
    });
  }

  /**
   * Render the trade row as a single horizontal line:
   *   [icon] ×N (+ [icon] ×N)* → [icon] ×N (+ [icon] ×N)*
   * Same-kind resources are aggregated so `[scroll, scroll]` reads as
   * `[scroll-icon] ×2` instead of two separate "scroll" entries.
   */
  private renderTradeRow(offer: TradeOffer): void {
    let cursorX = 0;
    const cost = this.aggregate(offer.cost);
    const receive = this.aggregate(offer.receive);
    cost.forEach((g, i) => {
      if (i > 0) cursorX = this.appendSeparator(cursorX, "+");
      cursorX = this.appendResource(cursorX, g.resource, g.count, /* isCost */ true);
    });
    cursorX = this.appendSeparator(cursorX, "→", /* arrow */ true);
    receive.forEach((g, i) => {
      if (i > 0) cursorX = this.appendSeparator(cursorX, "+");
      cursorX = this.appendResource(cursorX, g.resource, g.count, /* isCost */ false);
    });
  }

  /** Combine same-kind resources (mostly relevant for `inventoryItem` which
   *  appears as N separate entries when N items are involved). Numeric
   *  resources keep their `amount`; only their count is aggregated. */
  private aggregate(resources: readonly Resource[]): { resource: Resource; count: number }[] {
    const out: { resource: Resource; count: number }[] = [];
    for (const r of resources) {
      if (r.kind === "inventoryItem") {
        const existing = out.find(
          (g) => g.resource.kind === "inventoryItem" && g.resource.itemId === r.itemId,
        );
        if (existing) {
          existing.count += 1;
          continue;
        }
      } else if (r.kind === "skill") {
        const existing = out.find(
          (g) => g.resource.kind === "skill" && g.resource.skillId === r.skillId,
        );
        if (existing) {
          existing.count += 1;
          continue;
        }
      }
      out.push({ resource: r, count: 1 });
    }
    return out;
  }

  private appendResource(cursorX: number, r: Resource, count: number, isCost: boolean): number {
    const icon = this.scene.add.image(cursorX, 0, iconKeyForResource(r)).setOrigin(0, 0);
    const label = this.scene.add
      .text(cursorX + 9, 4, resourceLabel(r, count), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.ink),
        fontStyle: "bold",
      })
      .setOrigin(0, 0.5);
    this.tradeRow.add([icon, label]);
    if (isCost && isSkillResource(r)) {
      const outline = this.scene.add
        .rectangle(cursorX - 1, -1, 10, 10, 0xff4d4d, 0)
        .setOrigin(0, 0)
        .setStrokeStyle(1, 0xff4d4d);
      this.tradeRow.add(outline);
    }
    const labelWidth = Math.max(10, resourceLabel(r, count).length * 5);
    return cursorX + 9 + labelWidth + 4;
  }

  private appendSeparator(cursorX: number, text: string, arrow = false): number {
    // Center vertically on the icon row (icons are 8 px tall, top at y=0).
    // The "→" glyph sits low inside its line box, so using origin(0, 0.5)
    // + y=4 lines its centroid up with the icon centers cleanly.
    const sep = this.scene.add
      .text(cursorX, 4, text, {
        fontFamily: "monospace",
        fontSize: arrow ? "11px" : "9px",
        color: this.toHex(arrow ? PALETTE.shopCardEdge : PALETTE.ink),
        fontStyle: arrow ? "bold" : "normal",
      })
      .setOrigin(0, 0.5);
    this.tradeRow.add(sep);
    return cursorX + (arrow ? 12 : 8);
  }

  private startBob(): void {
    if (this.bobTween) return;
    this.bobTween = this.scene.tweens.add({
      targets: this.container,
      y: this.container.y - 1,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  private stopBob(): void {
    if (this.bobTween) {
      this.bobTween.stop();
      this.bobTween = undefined;
    }
  }

  private truncateName(name: string): string {
    return name.length <= 12 ? `@${name}` : `@${name.slice(0, 11)}…`;
  }

  private toHex(c: number): string {
    return `#${c.toString(16).padStart(6, "0")}`;
  }
}

export const OFFER_CARD_WIDTH = CARD_WIDTH;
export const OFFER_CARD_HEIGHT = CARD_HEIGHT;
